import type { ReactNode } from 'react';
import { useVerificationEmailConfig } from '../../features/settings';
import { formatDateTime } from '../../lib/format';

const PROVIDER_OPTIONS = [
  { value: 'disabled', label: '禁用' },
  { value: 'auto', label: '自动优先阿里云' },
  { value: 'aliyun', label: '强制阿里云 DirectMail' },
  { value: 'tencent', label: '强制腾讯云 SES' },
] as const;

export function VerificationEmailPage() {
  const {
    data,
    form,
    loading,
    saving,
    deletingProvider,
    error,
    setProvider,
    updateAliyunField,
    updateTencentField,
    save,
    removeProvider,
    reload,
  } = useVerificationEmailConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-red-500 mb-3">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl space-y-6">
      <div className="space-y-2">
        <p className="text-sm text-gray-500">
          管理注册、登录、提现等邮箱验证码的发送通道。保存后无需重启，下一次发码会立即使用最新配置。
        </p>
        {data?.using_env_fallback && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            当前尚未写入数据库运行时配置，页面展示的是环境变量基线。首次保存后，后续发码将优先使用后台保存的配置。
          </div>
        )}
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div>
          <h2 className="text-base font-medium text-gray-900">当前发送策略</h2>
          <p className="text-sm text-gray-500 mt-1">
            `auto` 会优先尝试阿里云，阿里云未配置完整时再切到腾讯云。
          </p>
        </div>
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-1">验证码邮件 provider</label>
          <select
            value={form.provider}
            onChange={(e) => setProvider(e.target.value as typeof form.provider)}
            className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            {PROVIDER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-medium text-gray-900">阿里云 DirectMail</h2>
            <p className="text-sm text-gray-500 mt-1">
              当前状态：{data?.aliyun.ready ? '配置完整，可用于发送' : '配置未完成'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => removeProvider('aliyun')}
            disabled={deletingProvider === 'aliyun'}
            className="text-sm text-red-500 hover:text-red-600 disabled:opacity-50"
          >
            {deletingProvider === 'aliyun' ? '删除中...' : '删除阿里云配置'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="AccessKeyId">
            <input
              type="text"
              value={form.aliyun.access_key_id}
              onChange={(e) => updateAliyunField('access_key_id', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="AccessKeySecret">
            <input
              type="password"
              value={form.aliyun.access_key_secret}
              onChange={(e) => updateAliyunField('access_key_secret', e.target.value)}
              className={inputCls}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Region">
            <input
              type="text"
              value={form.aliyun.region}
              onChange={(e) => updateAliyunField('region', e.target.value)}
              className={inputCls}
              placeholder="ap-southeast-1"
            />
          </Field>
          <Field label="AccountName">
            <input
              type="text"
              value={form.aliyun.account_name}
              onChange={(e) => updateAliyunField('account_name', e.target.value)}
              className={inputCls}
              placeholder="no-reply@example.com"
            />
          </Field>
          <Field label="FromAlias">
            <input
              type="text"
              value={form.aliyun.from_alias}
              onChange={(e) => updateAliyunField('from_alias', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="TagName">
            <input
              type="text"
              value={form.aliyun.tag_name}
              onChange={(e) => updateAliyunField('tag_name', e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.aliyun.reply_to_address}
            onChange={(e) => updateAliyunField('reply_to_address', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          启用 ReplyToAddress
        </label>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-medium text-gray-900">腾讯云 SES</h2>
            <p className="text-sm text-gray-500 mt-1">
              当前状态：{data?.tencent.ready ? '配置完整，可用于发送' : '配置未完成'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => removeProvider('tencent')}
            disabled={deletingProvider === 'tencent'}
            className="text-sm text-red-500 hover:text-red-600 disabled:opacity-50"
          >
            {deletingProvider === 'tencent' ? '删除中...' : '删除腾讯云配置'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="SecretId">
            <input
              type="text"
              value={form.tencent.secret_id}
              onChange={(e) => updateTencentField('secret_id', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="SecretKey">
            <input
              type="password"
              value={form.tencent.secret_key}
              onChange={(e) => updateTencentField('secret_key', e.target.value)}
              className={inputCls}
              autoComplete="new-password"
            />
          </Field>
          <Field label="Region">
            <input
              type="text"
              value={form.tencent.region}
              onChange={(e) => updateTencentField('region', e.target.value)}
              className={inputCls}
              placeholder="ap-singapore"
            />
          </Field>
          <Field label="FromEmailAddress">
            <input
              type="text"
              value={form.tencent.from_email_address}
              onChange={(e) => updateTencentField('from_email_address', e.target.value)}
              className={inputCls}
              placeholder="support@tyg.cc"
            />
          </Field>
          <Field label="ReplyToAddresses">
            <input
              type="text"
              value={form.tencent.reply_to_addresses}
              onChange={(e) => updateTencentField('reply_to_addresses', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Subject">
            <input
              type="text"
              value={form.tencent.subject}
              onChange={(e) => updateTencentField('subject', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="TemplateID">
            <input
              type="number"
              min={0}
              value={form.tencent.template_id}
              onChange={(e) => updateTencentField('template_id', Number(e.target.value) || 0)}
              className={inputCls}
            />
          </Field>
          <Field label="TriggerType">
            <input
              type="number"
              min={0}
              value={form.tencent.trigger_type}
              onChange={(e) => updateTencentField('trigger_type', Number(e.target.value) || 0)}
              className={inputCls}
            />
          </Field>
          <Field label="TemplateCodeKey">
            <input
              type="text"
              value={form.tencent.template_code_key}
              onChange={(e) => updateTencentField('template_code_key', e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
      </section>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
        >
          {saving && (
            <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          )}
          保存并立即生效
        </button>
      </div>

      {data && (
        <div className="pt-4 border-t border-gray-200 text-xs text-gray-400 space-y-1">
          {data.updated_at && <p>最后更新：{formatDateTime(data.updated_at)}</p>}
          {data.updated_by != null && <p>操作人 ID：{data.updated_by}</p>}
        </div>
      )}
    </div>
  );
}

function Field(props: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{props.label}</span>
      {props.children}
    </label>
  );
}

const inputCls =
  'h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary';
